import clsx from 'clsx';
import { forwardRef, ReactNode, Ref } from 'react';

type Theme = 'action' | 'accent' | 'default' | 'text' | 'sand';
type TypeCase = 'upper' | 'capitalize';

interface Props extends BaseProps {
  typeCasing?: TypeCase;
  className?: string;
  color?: string;
  endIcon?: ReactNode;
  fullWidth?: boolean;
  minWidth?: number;
  padding?: StandardSizes | 'none';
  rounded?: boolean;
  startIcon?: ReactNode;
  theme?: Theme;
  type?: 'submit' | 'reset' | 'button';
  width?: number;
  [x: string]: any;
}

export const Button = forwardRef(function Button(
  props: Props,
  ref: Ref<HTMLButtonElement>
) {
  const {
    className,
    children,
    color: customColor,
    endIcon = null,
    fullWidth,
    width: customWidth,
    minWidth: customMinWidth,
    padding = 'md',
    rounded = false,
    startIcon = null,
    textColor,
    theme = 'default',
    type = 'button',
    typeCasing = 'upper',
    ...rest
  } = props;
  const classes = useStyles();
  const shape = rounded ? 'full' : 'default';
  const width = fullWidth ? 'full' : 'default';

  const customStyles = {
    color: customColor,
    width: valueToPixel(customWidth),
    minWidth: valueToPixel(customMinWidth),
  };

  const generateStyles = (): object | undefined => {
    if (Object.values(customStyles).length === 0) return undefined;
    return Object.assign({}, customStyles);
  };

  const getClassValues = (className: object): string[] =>
    Object.values(className);

  const typeClasses: string[] = [classes.type.casing[typeCasing]];
  const themeClasses: string[] = [...getClassValues(classes.theme[theme])];

  return (
    <button
      ref={ref}
      className={clsx(
        className,
        classes.root,
        classes.padding[padding],
        classes.rounded[shape],
        classes.width[width],
        ...typeClasses,
        ...themeClasses
      )}
      style={generateStyles()}
      type={type}
      {...rest}
    >
      {startIcon}
      {children}
      {endIcon}
    </button>
  );
});

const valueToPixel = (value: number | undefined) => {
  if (typeof value === 'number') return `${value}px`;
  return value;
};

const useStyles = () => ({
  root: 'flex flex-row items-center justify-center gap-2 appearance-none h-fit disabled:pointer-events-none whitespace-nowrap transition ease-in-out duration-300',
  width: {
    default: 'w-fit',
    full: 'w-full',
  },
  padding: {
    sm: 'px-1 py-1',
    md: 'px-4 py-2',
    lg: 'px-6 py-2',
    none: 'p-0',
  },
  rounded: {
    default: 'rounded',
    full: 'rounded-full',
  },
  theme: {
    accent: {
      base: 'border border-primary text-primary',
      hover: 'hover:bg-primary hover:text-white',
      disabled:
        'disabled:border-border-light disabled:dark:border-border-dark disabled:text-inherit disabled:opacity-30',
    },
    action: {
      base: 'bg-background-dark text-white active:scale-95',
      hover:
        'hover:bg-background-dark/90 hover:dark:bg-[#282D31] hover:text-opacity-80',
      disabled: 'disabled:opacity-50',
    },
    text: {
      base: 'bg-transparent text-primary !p-0',
      hover: 'hover:opacity-80 hover:text-primary',
      disabled: 'disabled:opacity-30',
    },
    default: {
      base: 'bg-gray-300 text-gray-900',
      hover: 'hover:dark:bg-white hover:bg-gray-200',
      disabled: 'disabled:opacity-30',
    },
    sand: {
      base: 'bg-sand text-background-dark',
      hover: 'hover:opacity-80',
      disabled: 'disabled:opacity-30',
    },
  },
  type: {
    casing: {
      upper: 'uppercase',
      capitalize: 'capitalize',
    },
  },
});
